/**
 * WCS bounds — shared kind-agnostic geometry math (T1 arm (c)).
 *
 * Every per-kind bounds function in `./wcsBounds.ts` reduces an entity to a set of
 * OCS-LOCAL critical 3D points (exact extrema for the common cases, a dense sample
 * for curves under a non-default OCS — see `arcExtentPoints2D`/`ellipseExtentPoints2D`'s
 * docs), then hands them to `aabb3FromLocalPoints`, which applies the ONE-TRUE OCS→WCS
 * transform (the Autodesk "Arbitrary Axis Algorithm") and reduces to an axis-aligned box.
 * Keeping the OCS transform in exactly one place means every kind gets Z / extrusion
 * fidelity "for free" — a kind file only has to get its LOCAL 2D/3D critical points right.
 *
 * Reference sources (CLAUDE.md order): Autodesk DXF reference ("OCS and Extrusion
 * Direction" / "Arbitrary Axis Algorithm"), `ezdxf.math.OCS` (same algorithm, same
 * 1/64 threshold), and this repo's existing partial implementations:
 *   - `apps/web/src/app/print/pdf/converters/ocs.ts::ocsToWcs2d` — the ONLY other
 *     Arbitrary-Axis implementation in the repo. It is a 2D-projection variant (drops
 *     world Z — it flattens an OCS-planar entity onto an orthographic top-down PDF
 *     plot) and lives in `apps/web` (a consumer of `packages/*`, so `drawing-model`
 *     cannot import it — wrong dependency direction; `.dependency-cruiser.cjs` forbids
 *     a package importing an app). This module is the FULL 3D point-transform sibling
 *     (returns a real `{x,y,z}` WCS point, not a 2D-projected plot coordinate) — a
 *     different consumer, not a re-derivation of the same output shape.
 *   - `packages/cad-core/src/common.ts` (`arcBoundsDeg`/`ellipseBoundsRad`/`sweepDeg`/
 *     `sweepRad`/…) implements the SAME 2D arc/ellipse extremum algorithm for cad-core's
 *     grips/select-geometry bounds (2D, no OCS, no Z). `cad-core` DEPENDS ON
 *     `drawing-model` (`package.json`), never the reverse, so this module cannot import
 *     that copy — re-deriving the (short, well-understood, DXF-spec-pinned) extremum
 *     formulas here is the same unavoidable-by-dependency-direction duplication already
 *     established for `isCustomMtextXAxisDirection`/`effectiveMtextAngleDeg`
 *     (`packages/cad-core/src/leafBounds.ts` — "cad-core cannot import command-engine
 *     … this tiny pure predicate is duplicated here rather than shared").
 *
 * `packages/drawing-model/src/viewportMembership.ts::entityModelBounds` computes a
 * DIFFERENT (2D-only, no-OCS, deliberately-conservative) bounds for a DIFFERENT purpose
 * (viewport-clip membership windowing) — its own doc says "Extrusion / OCS … are not
 * modelled (over-inclusion only)". This module is the dedicated, OCS/Z-aware WCS bounds
 * helper T1 arm (c) requires; the two are not interchangeable and neither replaces the
 * other (viewportMembership stays a fast conservative pre-filter for its own call site).
 */

import type { AttdefEntity, BlockDefinition, BlockInsertAttribute } from "./model";
import { cross, normalize } from "./vec3";

const EPS = 1e-9;
const TWO_PI = Math.PI * 2;

// ── 3D vector / AABB primitives ─────────────────────────────────────────────

/** A plain 3D point/vector — OCS-LOCAL or WCS depending on context (documented per call site). */
export interface Wcs3 {
  readonly x: number;
  readonly y: number;
  readonly z: number;
}

/** A plain 2D point — always OCS-LOCAL (the entity's own drawing plane, pre-OCS-transform). */
export interface Local2 {
  readonly x: number;
  readonly y: number;
}

/** Axis-aligned 3D WCS bounding box. Always fully populated (min ≤ max on every axis). */
export interface WcsAabb3 {
  minX: number;
  minY: number;
  minZ: number;
  maxX: number;
  maxY: number;
  maxZ: number;
}

/** A fresh empty accumulator (every bound at its identity extreme) for `extendAabb3`. */
export function emptyAabb3(): WcsAabb3 {
  return { minX: Infinity, minY: Infinity, minZ: Infinity, maxX: -Infinity, maxY: -Infinity, maxZ: -Infinity };
}

/** Whether `box` never received a finite point (still at its `emptyAabb3()` identity). */
export function isEmptyAabb3(box: WcsAabb3): boolean {
  return !(box.minX <= box.maxX);
}

/** Extend `box` (in place) to include `p`. Non-finite coordinates are skipped (never poison the box). */
export function extendAabb3(box: WcsAabb3, p: Wcs3): void {
  if (!Number.isFinite(p.x) || !Number.isFinite(p.y) || !Number.isFinite(p.z)) return;
  if (p.x < box.minX) box.minX = p.x;
  if (p.y < box.minY) box.minY = p.y;
  if (p.z < box.minZ) box.minZ = p.z;
  if (p.x > box.maxX) box.maxX = p.x;
  if (p.y > box.maxY) box.maxY = p.y;
  if (p.z > box.maxZ) box.maxZ = p.z;
}

/** Union two non-empty AABBs. */
export function unionAabb3(a: WcsAabb3, b: WcsAabb3): WcsAabb3 {
  return {
    minX: Math.min(a.minX, b.minX),
    minY: Math.min(a.minY, b.minY),
    minZ: Math.min(a.minZ, b.minZ),
    maxX: Math.max(a.maxX, b.maxX),
    maxY: Math.max(a.maxY, b.maxY),
    maxZ: Math.max(a.maxZ, b.maxZ),
  };
}

// ── OCS ↔ WCS — the Autodesk "Arbitrary Axis Algorithm" ────────────────────

const ARBITRARY_AXIS_THRESHOLD = 1 / 64;

/** Whether `e` is the DXF default extrusion (absent, or numerically (0,0,1)) — the "no OCS tilt" case. */
export function isDefaultExtrusion(e?: { x: number; y: number; z: number }): boolean {
  if (e === undefined) return true;
  return Math.abs(e.x) < EPS && Math.abs(e.y) < EPS && Math.abs(e.z - 1) < EPS;
}

/**
 * Near-zero tolerance for the OCS handedness Z test — absorbs the floating-point noise a planar
 * reflection leaves on the Z component (mirroring an in-plane normal can leave `z = −1.2e-16`,
 * and a `−0` Z must read as NOT mirrored).
 */
const OCS_HANDEDNESS_EPS = 1e-9;

/**
 * Does the OCS extrusion NORMAL describe a REFLECTED (left-handed) frame when the entity is
 * drawn TOP-DOWN (the editor's world-XY projection)? `det([ocsX_xy | ocsY_xy]) = n.z` exactly
 * (the Arbitrary Axis Algorithm's own identity — see `command-engine/geometryTransforms.ts
 * ::isReflectedOcs` for the full derivation and the `z ≈ 0` edge-on case, which is
 * geometrically UNDEFINED and therefore reported as NOT reflected), so the sign of `z` IS the
 * handedness bit: `(0,0,−1)` — what MIRROR writes onto a default MTEXT — is reflected.
 *
 * It lives HERE, in the lowest package, because THREE layers need the same bit and none of them
 * may import `command-engine`: the render adapter (`mtextFromEntity.ts`, which turns it into the
 * `mesh.scale.set(1, −1, 1)` flip `sceneTextMeshFactories.ts::applyMirrorScale` applies), this
 * package's MTEXT bounds ({@link wcsBoundsText.mtextAnchoredBoxCorners}, whose painted rectangle
 * flips with the mesh) and `cad-core`'s select geometry. `command-engine`'s own
 * `isReflectedOcs` now DELEGATES here rather than carrying a second copy of the comparison —
 * the same relocation `./vec3.ts` records for the shared 3D vector math (CLAUDE.md dup:check
 * one-truth), and the same reason: `drawing-model` is below `command-engine` in the dependency
 * graph, never the reverse.
 */
export function isReflectedOcs(normal: { readonly z?: number } | undefined): boolean {
  if (!normal) return false;
  return (normal.z ?? 0) < -OCS_HANDEDNESS_EPS;
}

/** The resolved OCS basis {Ax, Ay, Az} a local `(x,y,z)` point is expressed in. */
export interface OcsAxes {
  readonly ax: Wcs3;
  readonly ay: Wcs3;
  readonly az: Wcs3;
}

/**
 * Normalize, with the OCS-specific degenerate fallback (`{0,0,1}`, the WCS default) instead of
 * the shared `normalize`'s generic `{0,0,0}` zero-vector marker — an OCS basis vector must never
 * be all-zero. Delegates the actual math to `./vec3`'s one-truth `normalize` (dup:check —
 * one-truth cross-product/length/normalize math shared with `command-engine`'s re-exported copy).
 */
function normalize3(v: Wcs3): Wcs3 {
  const n = normalize(v);
  return n.x === 0 && n.y === 0 && n.z === 0 ? { x: 0, y: 0, z: 1 } : n;
}

/** The identity OCS basis — the WCS-aligned default, reused for every default-extrusion entity. */
const IDENTITY_OCS_AXES: OcsAxes = {
  ax: { x: 1, y: 0, z: 0 },
  ay: { x: 0, y: 1, z: 0 },
  az: { x: 0, y: 0, z: 1 },
};

/**
 * The Autodesk "Arbitrary Axis Algorithm" (DXF reference, "OCS and Extrusion Direction"):
 * given the extrusion (OCS normal) `N`, compute the OCS basis {Ax, Ay, Az=N}. Matches
 * `ezdxf.math.OCS` and this repo's existing `apps/web/…/converters/ocs.ts::ocsToWcs2d`
 * bit-for-bit (same 1/64 threshold, same seed-axis choice). An absent / default (0,0,1)
 * extrusion short-circuits to the identity basis (the overwhelming common case — a
 * planar 2D drawing has no OCS tilt at all).
 */
export function computeOcsAxes(extrusion?: { x: number; y: number; z: number }): OcsAxes {
  if (isDefaultExtrusion(extrusion)) return IDENTITY_OCS_AXES;
  const az = normalize3(extrusion!);
  const seed: Wcs3 =
    Math.abs(az.x) < ARBITRARY_AXIS_THRESHOLD && Math.abs(az.y) < ARBITRARY_AXIS_THRESHOLD
      ? { x: 0, y: 1, z: 0 } // World-Y × N
      : { x: 0, y: 0, z: 1 }; // World-Z × N
  const ax = normalize3(cross(seed, az));
  const ay = normalize3(cross(az, ax));
  return { ax, ay, az };
}

/** Transform one OCS-local point `(x,y,z)` into WCS via the basis `{ax,ay,az}`. */
export function ocsToWcs(local: Wcs3, axes: OcsAxes): Wcs3 {
  return {
    x: local.x * axes.ax.x + local.y * axes.ay.x + local.z * axes.az.x,
    y: local.x * axes.ax.y + local.y * axes.ay.y + local.z * axes.az.y,
    z: local.x * axes.ax.z + local.y * axes.ay.z + local.z * axes.az.z,
  };
}

/**
 * The shared "finalize" step every kind file ends with: reduce a set of OCS-LOCAL
 * critical 3D points to a WCS `WcsAabb3` by transforming each through the extrusion's
 * OCS basis and accumulating. `null` when `points` is empty (nothing to bound).
 */
export function aabb3FromLocalPoints(
  points: readonly Wcs3[],
  extrusion?: { x: number; y: number; z: number },
): WcsAabb3 | null {
  if (points.length === 0) return null;
  const axes = computeOcsAxes(extrusion);
  const box = emptyAabb3();
  for (const p of points) extendAabb3(box, ocsToWcs(p, axes));
  return isEmptyAabb3(box) ? null : box;
}

/**
 * Normalize a degenerate (non-finite / ~zero) INSERT scale component to `1` — the SAME guard the
 * render path applies before flattening a Block (INSERT) so a schema-valid `scaleX/scaleY/scaleZ:
 * 0` (`blockInsertEntitySchema` carries no positivity constraint) renders at UNIT scale rather
 * than collapsing to a degenerate line/plane (@codex T1 arm(c) round-19 P2 finding). Reference
 * source: `apps/web/src/app/scene3d/adapters/typed/blockFromEntity.ts::guardScale` — "Legacy scale
 * guard (`flattenBlockInsertEntity:1080-1084`): a degenerate (non-finite / ~zero) component falls
 * back to 1" (same `1e-9` threshold, same `1` fallback). `packages/drawing-model` cannot import
 * `apps/web` (wrong dependency direction — a package importing an app — forbidden by
 * `.dependency-cruiser.cjs`), so this tiny, DXF-spec-independent formula is re-applied here rather
 * than shared — dup:check convergence on one documented source, not an independent re-derivation
 * (same precedent this module's own header already establishes for `computeOcsAxes`/
 * `ellipsePointAtDecomposed`). Every bounds computation that reads `scaleX`/`scaleY`/`scaleZ` off a
 * Block (INSERT) entity — the top-level insert-chain transform AND the MINSERT per-cell grid-delta
 * scale (`wcsBoundsBlock.ts`) — applies this guard so a zero-scale INSERT's `entityBounds` matches
 * the rendered UNIT-SCALE footprint instead of under-reporting it as a line/plane.
 */
export function guardInsertScale(value: number): number {
  return Number.isFinite(value) && Math.abs(value) > 1e-9 ? value : 1;
}

/**
 * `value > 0` (finite) else `fallback` — matches the render path's
 * `apps/web/src/app/scene3d/adapters/adapterContext.ts::positiveOr` EXACTLY (`value !== undefined
 * && Number.isFinite(value) && value > 0 ? value : fallback` — `Number.isFinite(undefined)` is
 * already `false`, so dropping the explicit `undefined` check changes nothing observable),
 * confirmed live at `apps/web/src/app/scene3d/adapters/typed/textFromEntity.ts:98,100`:
 * `positiveOr(entity.height, 2.5)` / `positiveOr(entity.widthFactor, 1)`. Re-applied here rather
 * than imported for the same reason {@link guardInsertScale} above it is — `packages/drawing-model`
 * cannot import `apps/web`.
 *
 * @codex T1 arm(c) round-21 P2 finding (F2): `wcsBoundsText.ts::textJustifiedLocalBox` used to
 * clamp `height` to 0 and pass `widthFactor` through unclamped, so a valid annotative TEXT/ATTDEF
 * (`height === 0`) or a non-positive `widthFactor` got a near-degenerate box instead of the render
 * path's 2.5/1 fallback. It sits HERE, beside the other re-applied render-path guards, rather than
 * in the one text module that first needed it: `wcsBoundsLeaf.ts::textLocal`/`mtextLocal` need the
 * same clamp for each `annotativeContexts[]` entry's per-scale height, and the formula knows
 * nothing about text (round-23 P2, F8 exported it; round 11 moved it, keeping ONE definition).
 */
export function positiveOr(value: number, fallback: number): number {
  return Number.isFinite(value) && value > 0 ? value : fallback;
}

/** Structural shape both the parsed `mleaderBlockAttributeRefSchema` row and any equivalent
 *  encoded-form record satisfy (`attDefHandle`/`index`/`width`/`value` — DXF group 330/177/44/302). */
export interface BlockAttrRefLike {
  attDefHandle: string;
  index: number;
  width: number;
  value: string;
}

/**
 * Map a BLOCK-content MLEADER's `blockAttributeReferences[]` (keyed by ATTDEF HANDLE, DXF group
 * 330 soft pointer) to a synthetic `BlockInsertAttribute[]` keyed by TAG — the only key
 * `attdefFromEntity.ts`'s Phase A tag match (render path) AND `wcsBoundsBlock.ts`'s
 * `resolveAttributeContribution` (bounds path) both understand (@codex T1 arm(c) round-19 P2
 * finding — F9). Two-pass resolution, mirroring the SVG twin `renderMultileader.tsx`'s
 * `attrLookup` order exactly:
 *
 *   1. Primary — each ATTDEF, in BLOCK-DEFINITION ORDER, claims the first still-unclaimed ref
 *      whose `attDefHandle` equals ITS OWN `.handle` (DXF round-trips handles, so this is the
 *      stable, intended match).
 *   2. Fallback — a still-unclaimed ref whose `attDefHandle` is a real ATTDEF TAG (a legacy/
 *      malformed DXF back-pointer that doubles as a tag string).
 *
 * A ref matching NEITHER is skipped (never synthesizes a garbage-tag attribute — see the git
 * history of this helper's original home, `multileaderToPrimitives.ts`, for the fuller incident
 * writeup). Emitted in BLOCK-DEFINITION ATTDEF order (not incoming-ref order) so a consumer's own
 * occurrence-ordered duplicate-tag matching (e.g. `wcsBoundsBlock.ts` F7) stays aligned for a
 * duplicate-tag block.
 *
 * Originally two independent copies — one in this module (bounds), one in
 * `apps/web/src/app/scene3d/adapters/geom/multileaderGeometry.ts` (render). `apps/web` DEPENDS ON
 * `packages/drawing-model` (the normal app→package direction `.dependency-cruiser.cjs` allows),
 * so — unlike `guardInsertScale`'s app→package duplication above — this one converges to a SINGLE
 * shared implementation here, re-exported through the package barrel
 * (`packages/drawing-model/src/index.ts`) for `multileaderGeometry.ts` to import directly instead
 * of re-deriving (jscpd flagged the two-copy state as new duplication; this is the fix, not a
 * baseline exemption).
 */
export function mapBlockAttributeReferences(
  refs: readonly BlockAttrRefLike[],
  blockDef: BlockDefinition | undefined,
): BlockInsertAttribute[] {
  if (refs.length === 0) return [];
  const attdefs: AttdefEntity[] = blockDef ? blockDef.entities.filter((child): child is AttdefEntity => child.type === "Attdef") : [];
  const out: BlockInsertAttribute[] = [];
  const claimed = new Array<boolean>(refs.length).fill(false);
  // Pass 1 (PRIMARY) — handle match, in ATTDEF definition order.
  for (const attdef of attdefs) {
    if (!attdef.handle) continue;
    for (let i = 0; i < refs.length; i++) {
      if (claimed[i]) continue;
      if (refs[i]!.attDefHandle === attdef.handle) {
        out.push({ tag: attdef.tag, value: refs[i]!.value, handle: "" });
        claimed[i] = true;
        break;
      }
    }
  }
  // Pass 2 (FALLBACK) — tag match, still in ATTDEF definition order.
  for (const attdef of attdefs) {
    for (let i = 0; i < refs.length; i++) {
      if (claimed[i]) continue;
      if (refs[i]!.attDefHandle === attdef.tag) {
        out.push({ tag: attdef.tag, value: refs[i]!.value, handle: "" });
        claimed[i] = true;
        break;
      }
    }
  }
  return out;
}

// ── 2D arc extremum math (OCS-local plane, DEGREES, CCW — DXF group 50/51 convention) ──

/**
 * Signed CCW sweep (degrees) from `startDeg` to `endDeg`, normalised into (0, 360]. A
 * zero-length or full-turn delta is treated as a FULL circle (360), matching the DXF
 * ARC convention (always swept CCW start→end) and mirroring `cad-core/common.ts
 * ::sweepDeg`'s full-turn fix (a naive `(end-start) % 360` collapses `[0,360]` to a
 * point instead of a full circle).
 */
export function sweepDeg(startDeg: number, endDeg: number): number {
  const raw = endDeg - startDeg;
  if (Math.abs(raw) >= 360 - 1e-9) return 360;
  let sweep = ((raw % 360) + 360) % 360;
  if (Math.abs(raw) < 1e-9 || sweep < 1e-9) sweep = 360;
  return sweep;
}

/** Whether `angleDeg` lies on the CCW arc `[startDeg, startDeg + sweep]`. */
export function angleOnSweepDeg(angleDeg: number, startDeg: number, sweep: number): boolean {
  const rel = (((angleDeg - startDeg) % 360) + 360) % 360;
  return rel <= sweep + 1e-9;
}

/** Point on a circle of radius `r` about `(cx,cy)` at `angleDeg` (CCW from +X). */
export function polarPoint2D(cx: number, cy: number, r: number, angleDeg: number): Local2 {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

/**
 * The EXACT OCS-local 2D critical points of a circular arc `[startDeg, endDeg]`: both
 * endpoints plus every cardinal angle (0/90/180/270) the sweep actually crosses (the
 * arc bows outside the endpoint-only chord). Exact for the common (non-tilted-OCS)
 * case; `tessellateArc2D` supplements this for a tilted-OCS entity (see its doc).
 *
 * `knownSweepDeg` — the arc's sweep MAGNITUDE in degrees, when the caller already KNOWS it and
 * did not have to infer it from the angle pair. Omit it (the ARC entity / HATCH arc-edge callers)
 * and the sweep is derived by {@link sweepDeg}, whose documented DXF convention treats a
 * zero-length delta as a FULL 360° turn — correct for those kinds, where `startAngle === endAngle`
 * genuinely MEANS a full circle.
 *
 * That convention is DESTRUCTIVE for a caller that derived the angle pair from a sweep it already
 * had in hand: `sweepDeg`'s full-turn rule fires on `|endDeg − startDeg| < 1e-9`, so a real,
 * NEAR-ZERO sweep is indistinguishable from a deliberate full turn once the sweep has been thrown
 * away and re-inferred. `wcsBoundsLeaf.ts::segmentLocalPoints` is exactly that caller: a polyline
 * bulge of `1e-12` (just past `bulgeArc`'s own `|bulge| < 1e-12` straight-segment guard) over a
 * 100 000-unit chord resolves to `r = 2.5e16` and a sweep of `2.3e-10°`, which `sweepDeg` read as
 * 360 — so a near-straight segment reported ALL FOUR cardinal points of a radius-2.5e16 circle,
 * a box 5e16 across for a 1e5-long segment (~5·10¹¹ times too large). An over-report cannot drop a
 * visible entity, but it destroys every consumer that reads the box as a SIZE: ZOOM Extents /
 * fit-to-extents zooms to 5e16, the spatial broad phase matches the entity against every query,
 * and viewport culling can never cull it. A bulge can encode at most `|θ| = 4·atan(bulge) < 2π`,
 * so a bulge-derived arc is NEVER a full turn and must never be re-read as one — it passes its
 * exact `|signedSweep|` here instead.
 *
 * The two endpoint samples stay `polarPoint2D(startDeg)`/`polarPoint2D(endDeg)` in BOTH modes, so
 * an omitted `knownSweepDeg` is byte-identical to the pre-parameter behaviour for every existing
 * caller.
 */
export function arcExtentPoints2D(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
  knownSweepDeg?: number,
): Local2[] {
  const sweep = knownSweepDeg ?? sweepDeg(startDeg, endDeg);
  const pts: Local2[] = [polarPoint2D(cx, cy, r, startDeg), polarPoint2D(cx, cy, r, endDeg)];
  for (const cardinal of [0, 90, 180, 270]) {
    if (angleOnSweepDeg(cardinal, startDeg, sweep)) pts.push(polarPoint2D(cx, cy, r, cardinal));
  }
  return pts;
}

/**
 * A dense sample of `steps` points along the arc `[startDeg,endDeg]`, RADIUS-INFLATED so the
 * result is a PROVABLY conservative bound, not merely a hopeful approximation (@codex T1 arm(c)
 * round-3 P2 finding: "avoid claiming sampled arcs are conservative bounds" — a plain point-on-
 * the-curve sample is NOT provably conservative under an arbitrary further rotation, since the
 * true extremum can fall strictly BETWEEN two adjacent samples, in the "sagitta gap").
 *
 * `arcExtentPoints2D` alone gives the EXACT bound in the entity's own OCS plane; once that plane
 * is tilted into WCS by a non-default extrusion (or, for a block-definition child, further
 * rotated by the enclosing INSERT), the plane-local X/Y extrema are no longer guaranteed to be
 * the WCS X/Y/Z extrema of the transformed curve. Tessellating the curve and transforming EVERY
 * sample point through the further transform helps — but ONLY samples-at-the-true-radius do NOT
 * mathematically guarantee containment (the same flattening-based strategy `ezdxf.bbox` uses is
 * ALSO an approximation, not a hard guarantee, for the identical reason).
 *
 * THE FIX (a real proof, not more samples "for luck"): inflate the sampled radius to
 * `r / cos(halfStep)`, where `halfStep` is HALF the angular gap between adjacent samples. This is
 * the exact circumradius of the regular polygon, sampled at these `n` angles, whose APOTHEM
 * (inradius) equals `r` — i.e. the polygon these inflated samples' CONVEX HULL forms fully
 * CIRCUMSCRIBES (contains) the true radius-`r` circle/arc. Convex-hull containment is preserved
 * under ANY subsequent linear/affine transform (rotation, scale) — so the AABB of the
 * (transformed) inflated samples is GUARANTEED to contain the AABB of the (identically
 * transformed) true curve, in every rotated frame, not just the untransformed one. This is now a
 * proof, not a claim.
 */
export function tessellateArc2D(
  cx: number,
  cy: number,
  r: number,
  startDeg: number,
  endDeg: number,
  steps = 64,
  knownSweepDeg?: number,
): Local2[] {
  // `knownSweepDeg` — see {@link arcExtentPoints2D}'s own doc for why a caller that ALREADY holds
  // the sweep must pass it rather than let `sweepDeg` re-infer it from the angle pair (a near-zero
  // sweep is re-read as a FULL 360° turn, which here would tessellate a whole radius-2.5e16 circle
  // for a near-straight polyline segment).
  const sweep = knownSweepDeg ?? sweepDeg(startDeg, endDeg);
  const n = Math.max(8, steps);
  const halfStepRad = (sweep * Math.PI) / 180 / (2 * n);
  const inflatedR = r / Math.cos(halfStepRad);
  const pts: Local2[] = [];
  for (let i = 0; i <= n; i++) pts.push(polarPoint2D(cx, cy, inflatedR, startDeg + (sweep * i) / n));
  return pts;
}

// ── 2D ellipse extremum math (OCS-local plane, RADIANS — DXF group 41/42 convention) ──

/** Radians analogue of {@link sweepDeg} for the ELLIPSE start/end parameter (group 41/42). */
export function sweepRad(startRad: number, endRad: number): number {
  const raw = endRad - startRad;
  if (Math.abs(raw) >= TWO_PI - 1e-9) return TWO_PI;
  let sweep = ((raw % TWO_PI) + TWO_PI) % TWO_PI;
  if (Math.abs(raw) < 1e-9 || sweep < 1e-9) sweep = TWO_PI;
  return sweep;
}

/** Radians analogue of {@link angleOnSweepDeg}. */
export function angleOnSweepRad(angleRad: number, startRad: number, sweep: number): boolean {
  const rel = (((angleRad - startRad) % TWO_PI) + TWO_PI) % TWO_PI;
  return rel <= sweep + 1e-9;
}

/**
 * Ellipse (or elliptic-arc) point at parameter `t` (radians), from the ALREADY-DECOMPOSED
 * major/minor magnitudes + major-axis direction cosine/sine, per the DXF ELLIPSE
 * parametrisation `P(t) = C + majorAxisVector·cos(t) + minorAxisVector·sin(t)` where
 * `minorAxisVector` is `majorAxisVector` rotated +90° and scaled by `ratio`. The ONE-TRUTH
 * low-level primitive — `packages/cad-core/src/common.ts::ellipsePointAt` (2D grips/select
 * bounds, a DIFFERENT consumer with an IDENTICAL decomposed signature) re-exports this exact
 * function rather than re-deriving the formula (dup:check — cad-core already depends on
 * `drawing-model`, the correct import direction, unlike the arc/ellipse EXTENT algorithms
 * below which stay duplicated for the dependency-direction reasons this module's header
 * documents).
 */
export function ellipsePointAtDecomposed(
  cx: number,
  cy: number,
  majorMag: number,
  minorMag: number,
  cosF: number,
  sinF: number,
  t: number,
): Local2 {
  const ct = Math.cos(t);
  const st = Math.sin(t);
  return {
    x: cx + majorMag * ct * cosF - minorMag * st * sinF,
    y: cy + majorMag * ct * sinF + minorMag * st * cosF,
  };
}

/**
 * Ellipse (or elliptic-arc) point at parameter `t` (radians) from the RAW major-axis vector +
 * ratio (the OCS-local ELLIPSE entity's own fields) — decomposes into magnitude/cos/sin once,
 * then delegates to {@link ellipsePointAtDecomposed}.
 */
export function ellipsePointAt2D(
  cx: number,
  cy: number,
  majorX: number,
  majorY: number,
  ratio: number,
  t: number,
): Local2 {
  const majorMag = Math.hypot(majorX, majorY);
  if (majorMag < EPS) return { x: cx, y: cy };
  const cosF = majorX / majorMag;
  const sinF = majorY / majorMag;
  const minorMag = majorMag * ratio;
  return ellipsePointAtDecomposed(cx, cy, majorMag, minorMag, cosF, sinF, t);
}

/**
 * The EXACT OCS-local 2D critical points of an ellipse / elliptic arc over parameter
 * range `[startParam,endParam]` (radians, CCW): both endpoints plus the parametric
 * critical points (dx/dt=0, dy/dt=0 — the ellipse's own X/Y extrema) that fall on the
 * swept range. Exact for the common (non-tilted-OCS) case; see {@link tessellateArc2D}
 * for why a tilted OCS additionally needs {@link tessellateEllipse2D}.
 */
export function ellipseExtentPoints2D(
  cx: number,
  cy: number,
  majorX: number,
  majorY: number,
  ratio: number,
  startParam: number,
  endParam: number,
): Local2[] {
  const majorMag = Math.hypot(majorX, majorY);
  if (majorMag < EPS) return [{ x: cx, y: cy }];
  const cosF = majorX / majorMag;
  const sinF = majorY / majorMag;
  const minorMag = majorMag * ratio;
  const sweep = sweepRad(startParam, endParam);
  const pts: Local2[] = [
    ellipsePointAt2D(cx, cy, majorX, majorY, ratio, startParam),
    ellipsePointAt2D(cx, cy, majorX, majorY, ratio, endParam),
  ];
  // dx/dt = 0 ⇒ tan(t) = -(minorMag·sinF) / (majorMag·cosF); dy/dt = 0 ⇒ the perpendicular pair.
  const tx = Math.atan2(-minorMag * sinF, majorMag * cosF);
  const ty = Math.atan2(minorMag * cosF, majorMag * sinF);
  for (const t of [tx, tx + Math.PI, ty, ty + Math.PI]) {
    if (angleOnSweepRad(t, startParam, sweep)) pts.push(ellipsePointAt2D(cx, cy, majorX, majorY, ratio, t));
  }
  return pts;
}

/**
 * Dense ellipse/elliptic-arc sample — the tilted-OCS conservative supplement (see
 * {@link tessellateArc2D}'s doc for the full "provably conservative, not just densely sampled"
 * reasoning). The SAME `r/cos(halfStep)` circumscribing-polygon proof is EXACT for a circle
 * (constant curvature); an ellipse's curvature varies (highest at the ends of the minor axis),
 * so uniformly inflating `majorMag`/`minorMag` by that factor is a defensible but NOT perfectly
 * tight generalisation. `ELLIPSE_INFLATION_SAFETY` adds a further small multiplicative margin
 * (@codex T1 arm(c) round-3 P2 finding) so the claim stays honestly conservative for the
 * varying-curvature case too, at a negligible cost to tightness.
 */
const ELLIPSE_INFLATION_SAFETY = 1.5;

export function tessellateEllipse2D(
  cx: number,
  cy: number,
  majorX: number,
  majorY: number,
  ratio: number,
  startParam: number,
  endParam: number,
  steps = 64,
): Local2[] {
  const sweep = sweepRad(startParam, endParam);
  const n = Math.max(8, steps);
  const halfStep = sweep / (2 * n);
  const inflate = 1 + (1 / Math.cos(halfStep) - 1) * ELLIPSE_INFLATION_SAFETY;
  const pts: Local2[] = [];
  for (let i = 0; i <= n; i++) {
    pts.push(ellipsePointAt2D(cx, cy, majorX * inflate, majorY * inflate, ratio, startParam + (sweep * i) / n));
  }
  return pts;
}

// ── 3D ellipse extremum math (WCS-final points, RADIANS — DXF group 41/42 convention) ──

/**
 * The EXACT 3D critical points of a fully-general 3D parametric ellipse/elliptic-arc
 * `P(t) = center + majorAxis·cos(t) + minorAxis·sin(t)` over `[startParam,endParam]`
 * (radians, CCW) — both endpoints plus the PER-AXIS (x, y, AND z, independently) parametric
 * critical points (dx/dt=0, dy/dt=0, dz/dt=0) that fall on the swept range.
 *
 * Generalizes {@link ellipseExtentPoints2D}'s plane-local 2D formula to an ellipse whose
 * `majorAxis`/`minorAxis` are FULL 3D vectors, not a plane-local 2D pair assumed to lie in the
 * XY plane — the caller (`wcsBoundsLeaf.ts::ellipseLocal`) passes the ELLIPSE entity's real 3D
 * `majorAxisEndpoint − center` vector and the real 3D minor-axis vector (`extrusion × majorAxis`,
 * normalized, scaled by `ratio·|majorAxis|` — matching the render tessellator's OWN formula,
 * `apps/web/…/arcEllipseSpline.ts::tessellateEllipse`/`ellipseMinorUnit`) — @codex T1 arm(c)
 * round-22 P2 finding (F1): the round-21 fix (F4) widened the reported Z range by the correct
 * `hypot(majorZ, minorZ)` AMPLITUDE, but round-21 left X/Y computed from the OLD XY-only
 * `ellipseExtentPoints2D(majorX, majorY, …)` — which silently assumes the minor axis is a plain
 * 90°-in-plane rotation of `(majorX, majorY)`, true ONLY for the default `(0,0,1)` extrusion. A
 * genuinely tilted extrusion moves part (or all) of the minor axis's contribution OUT of X/Y and
 * INTO Z (see `ellipseLocal`'s updated doc for a worked example), which the 2D-only formula could
 * never see. This function replaces that XY-only formula with the SAME `P(t) = center +
 * majorAxis·cos(t) + minorAxis·sin(t)` the renderer actually draws, computed directly in 3D — not
 * a 2D computation with a separate Z pad bolted on.
 *
 * Each axis independently satisfies `f(t) = A·cos(t) + B·sin(t)` (`A = majorAxis[d]`,
 * `B = minorAxis[d]`) — critical at `tan(t) = B/A`, i.e. `t = atan2(B,A)` and its antipode
 * `t + π` (one max, one min) — the SAME per-axis extremum algebra `ellipseExtentPoints2D`
 * already uses for X/Y (`tx`/`ty` there), just applied independently to X, Y, AND Z here instead
 * of assuming the plane is XY-aligned. For the DEFAULT extrusion + a planar (`majorAxis.z === 0`)
 * major axis this reduces to BYTE-IDENTICAL X/Y output to `ellipseExtentPoints2D` (verified: with
 * `extrusion=(0,0,1)`, `minorAxis = (−majorAxis.y, majorAxis.x, 0)·ratio`, which substituted into
 * `x(t) = majorAxis.x·cos(t) + minorAxis.x·sin(t)` reduces to the identical
 * `majorX·cos(t) − majorY·ratio·sin(t)` `ellipseExtentPoints2D` computes) — the overwhelmingly
 * common planar case is unaffected by this fix, matching this module's established
 * regression-safety discipline.
 */
export function ellipseExtentPoints3D(center: Wcs3, majorAxis: Wcs3, minorAxis: Wcs3, startParam: number, endParam: number): Wcs3[] {
  const sweep = sweepRad(startParam, endParam);
  const pointAt = (t: number): Wcs3 => {
    const ct = Math.cos(t);
    const st = Math.sin(t);
    return {
      x: center.x + majorAxis.x * ct + minorAxis.x * st,
      y: center.y + majorAxis.y * ct + minorAxis.y * st,
      z: center.z + majorAxis.z * ct + minorAxis.z * st,
    };
  };
  const pts: Wcs3[] = [pointAt(startParam), pointAt(endParam)];
  const major: readonly number[] = [majorAxis.x, majorAxis.y, majorAxis.z];
  const minor: readonly number[] = [minorAxis.x, minorAxis.y, minorAxis.z];
  for (let d = 0; d < 3; d++) {
    const A = major[d]!;
    const B = minor[d]!;
    if (Math.abs(A) < EPS && Math.abs(B) < EPS) continue;
    const t0 = Math.atan2(B, A);
    for (const t of [t0, t0 + Math.PI]) {
      if (angleOnSweepRad(t, startParam, sweep)) pts.push(pointAt(t));
    }
  }
  return pts;
}

/**
 * Dense 3D ellipse/elliptic-arc sample — the tilted-OCS-block-child conservative supplement
 * (mirrors {@link tessellateEllipse2D}'s "provably conservative, not just densely sampled"
 * reasoning, generalized to 3D). `majorAxis`/`minorAxis` are always PERPENDICULAR 3D vectors
 * (the minor axis is `extrusion × majorAxis`, a cross product — always ⟂ `majorAxis` by
 * construction), so this is an authentic planar ellipse embedded in 3D via an ORTHOGONAL linear
 * map from its own 2D parameter plane — orthogonal maps preserve length/angle/convexity, so the
 * IDENTICAL `r/cos(halfStep)` circumscribing-polygon proof {@link tessellateArc2D} establishes for
 * a 2D circle, plus `tessellateEllipse2D`'s `ELLIPSE_INFLATION_SAFETY` margin for an ellipse's
 * non-constant curvature, transfers unchanged to this 3D-embedded case: inflating BOTH axis
 * vectors' contribution by the same `inflate` factor still convex-hull-circumscribes the true
 * curve, in whatever 3D plane it actually lies.
 */
export function tessellateEllipse3D(
  center: Wcs3,
  majorAxis: Wcs3,
  minorAxis: Wcs3,
  startParam: number,
  endParam: number,
  steps = 64,
): Wcs3[] {
  const sweep = sweepRad(startParam, endParam);
  const n = Math.max(8, steps);
  const halfStep = sweep / (2 * n);
  const inflate = 1 + (1 / Math.cos(halfStep) - 1) * ELLIPSE_INFLATION_SAFETY;
  const pts: Wcs3[] = [];
  for (let i = 0; i <= n; i++) {
    const t = startParam + (sweep * i) / n;
    const ct = Math.cos(t) * inflate;
    const st = Math.sin(t) * inflate;
    pts.push({
      x: center.x + majorAxis.x * ct + minorAxis.x * st,
      y: center.y + majorAxis.y * ct + minorAxis.y * st,
      z: center.z + majorAxis.z * ct + minorAxis.z * st,
    });
  }
  return pts;
}

// ── 2D rotation (OCS-local plane) ───────────────────────────────────────────

/** Rotate a 2D vector `(x,y)` by `angleDeg` (CCW, DXF convention). */
export function rotateDeg2D(x: number, y: number, angleDeg: number): Local2 {
  const a = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(a);
  const sin = Math.sin(a);
  return { x: x * cos - y * sin, y: x * sin + y * cos };
}

// ── MINSERT grid ─────────────────────────────────────────────────────────

/**
 * The bounds-SUFFICIENT `(col, row)` OCS-local EXTREMAL CORNER offsets (BEFORE the insert's own
 * scale/rotate — the same block-local, pre-scale space the block's own geometry lives in) of a
 * `cols × rows` MINSERT grid, per DXF INSERT group 70/71/44/45 ("Array" fields). A plain INSERT
 * (`cols<=1 && rows<=1`) returns a single `(0,0)` offset; otherwise up to 4 distinct corners
 * (fewer when `cols<=1` or `rows<=1` collapses one axis to a single value).
 *
 * @codex T1 arm(c) round-10 P2 finding: previously enumerated EVERY one of `cols·rows` cell
 * offsets before this function's sole caller (`applyFullInsertChain`) reduced them to an AABB —
 * a schema-valid but pathological MINSERT (e.g. 10,000×10,000) would materialize 100M offset
 * objects (and then run the full per-point transform for EACH), turning an opt-in bounds query
 * into a hang/OOM. Only the 4 corners are needed because the full per-point-per-cell transform
 * (`wcsBoundsBlock.ts::transformOnePoint`: `A·((p − basePoint) + gridOffset) + insertionPoint`,
 * for a shared LINEAR map `A` = scale·rotate) is a MINKOWSKI SUM decomposition — additive in
 * `gridOffset` and `(p − basePoint)` separately, under one shared affine map. The axis-aligned
 * bounding box of a Minkowski sum is EXACTLY the per-axis sum of its two operands' own AABBs
 * (`min(X+Y) = min(X)+min(Y)`, ALWAYS — not an approximation, a general fact about sums of
 * independent ranges), and a LINEAR map satisfies `A(convexHull(S)) = convexHull(A(S))` — so
 * enumerating every INTERIOR grid cell contributes nothing the 4 (axis-aligned rectangle) corners
 * don't already bound. The identical argument applies to a nested/top-level Block's per-cell
 * `overridePosition`-set ATTRIB tiling (`wcsBoundsBlock.ts::tileAcrossMinsertCells`), which reuses
 * these same corner offsets for the SAME reason.
 *
 * Confirmed against three independent renderer sites (`scene/blockMatrix.ts::translate`,
 * `scene3d/adapters/typed/blockTransform.ts::transformBlockDirection`,
 * `print/pdf/converters/insertConverter.ts`) for the underlying per-cell OFFSET FORMULA (each
 * enumerates every cell for actual RENDERING — a materially different need, since every cell's
 * geometry must be individually drawn there, unlike this bounds-only helper's single caller,
 * which only ever reduces the result to an AABB).
 */
export function minsertGridOffsets2D(
  cols: number,
  rows: number,
  columnSpacing: number,
  rowSpacing: number,
): Local2[] {
  const c = Math.max(1, Math.round(cols));
  const r = Math.max(1, Math.round(rows));
  const maxX = (c - 1) * columnSpacing;
  const maxY = (r - 1) * rowSpacing;
  const xs = maxX === 0 ? [0] : [0, maxX];
  const ys = maxY === 0 ? [0] : [0, maxY];
  const offsets: Local2[] = [];
  for (const x of xs) {
    for (const y of ys) offsets.push({ x, y });
  }
  return offsets;
}

// ── MULTILEADER spline leader (Catmull-Rom → cubic Bézier) ─────────────────

/** One cubic Bézier segment of a SPLINE-type MLEADER leader line — see {@link splineLeaderCubics}. */
export interface SplineLeaderCubic {
  readonly p0: Wcs3;
  readonly c1: Wcs3;
  readonly c2: Wcs3;
  readonly p3: Wcs3;
}

/**
 * The cubic-Bézier segments a SPLINE-type (`leaderLineType === 2`) MLEADER leader line bows OUT
 * to — one cubic per consecutive vertex pair, tangents drawn from the Catmull-Rom neighbourhood
 * (interior: `(P[i+1] − P[i−1]) / 6`; reflective at each open end: `(P[end] − P[end∓1]) / 3`).
 * `[]` for a straight (<3-vertex) leader (drawn as a plain polyline, no curve — the raw-vertex
 * hull is already exact).
 *
 * ONE TRUTH shared by BOTH `packages/cad-core` (`select.ts`'s on-curve crossing-test tessellation
 * via `splineLeaderPolyline`, `leafBounds.ts`'s `multileaderBounds` via
 * {@link splineLeaderControlPoints}) AND this package's own `wcsBounds.ts`/`wcsBoundsBlock.ts`
 * bounds — `cad-core` DEPENDS ON `drawing-model` (never the reverse), so this is the correct
 * (and only) direction for math BOTH packages need: defined once here, `cad-core` imports it
 * (mirrors the established `ellipsePointAtDecomposed`/`mtextBoxSize`/`Vec3` precedent — dup:check
 * convergence, not a re-derivation).
 */
export function splineLeaderCubics(vertices: readonly (Local2 & { readonly z?: number })[]): SplineLeaderCubic[] {
  const n = vertices.length;
  if (n < 3) return [];
  const cubics: SplineLeaderCubic[] = [];
  for (let i = 0; i < n - 1; i++) {
    const p0 = i === 0 ? vertices[0]! : vertices[i - 1]!;
    const p1 = vertices[i]!;
    const p2 = vertices[i + 1]!;
    const p3 = i + 2 < n ? vertices[i + 2]! : vertices[n - 1]!;
    const p0z = p0.z ?? 0;
    const p1z = p1.z ?? 0;
    const p2z = p2.z ?? 0;
    const p3z = p3.z ?? 0;
    const t1x = i === 0 ? (p2.x - p1.x) / 3 : (p2.x - p0.x) / 6;
    const t1y = i === 0 ? (p2.y - p1.y) / 3 : (p2.y - p0.y) / 6;
    // @codex T1 arm(c) round-18 P2 finding: Z follows the IDENTICAL Catmull-Rom tangent formula
    // as X/Y (a Catmull-Rom spline is per-axis-separable) instead of being hardcoded to 0 — a
    // 3D-vertex spline leader's off-curve control points can extend beyond the raw vertices' own
    // Z range, under-reporting entityBounds' minZ/maxZ.
    const t1z = i === 0 ? (p2z - p1z) / 3 : (p2z - p0z) / 6;
    const t2x = i + 2 >= n ? (p2.x - p1.x) / 3 : (p3.x - p1.x) / 6;
    const t2y = i + 2 >= n ? (p2.y - p1.y) / 3 : (p3.y - p1.y) / 6;
    const t2z = i + 2 >= n ? (p2z - p1z) / 3 : (p3z - p1z) / 6;
    cubics.push({
      p0: { x: p1.x, y: p1.y, z: p1z },
      c1: { x: p1.x + t1x, y: p1.y + t1y, z: p1z + t1z },
      c2: { x: p2.x - t2x, y: p2.y - t2y, z: p2z - t2z },
      p3: { x: p2.x, y: p2.y, z: p2z },
    });
  }
  return cubics;
}

/**
 * The OFF-CURVE cubic-Bézier control points (c1/c2 of every cubic — see
 * {@link splineLeaderCubics}) a SPLINE leader line bows OUT to. p0/p3 of every cubic ARE the raw
 * vertices themselves (the caller already unions those separately), so only c1/c2 need adding
 * here — a cubic Bézier never leaves its own {p0,c1,c2,p3} convex hull, so unioning these is a
 * tight, ANALYTIC bound of the curved leader, never a sample-based under-report.
 *
 * @codex T1 arm(c) round-12 P2 finding: the prior MLEADER bounds accumulated only raw
 * `line.vertices`, missing this bow-out entirely for a spline-type leader.
 */
export function splineLeaderControlPoints(vertices: readonly (Local2 & { readonly z?: number })[]): Wcs3[] {
  const pts: Wcs3[] = [];
  for (const c of splineLeaderCubics(vertices)) pts.push(c.c1, c.c2);
  return pts;
}

/**
 * # LOCAL (anchor-relative, PRE-rotation) rectangle primitives
 *
 * `LocalRect` / {@link unionRect} / {@link rectCorners} / {@link localBoxRect} /
 * {@link anchorAndRotate} are the kind-agnostic box algebra every text-like corner builder in
 * `./wcsBoundsText.ts` composes — anchor a `width × height` rect by a col/row grid cell, union
 * two of them, turn one into corners, rotate + translate the result ONCE at the end. They live
 * here, with this module's other shared OCS/AABB primitives, rather than in the one file that
 * happens to call them today: none of them knows anything about text, and `wcsBoundsText.ts` is
 * AT its 2 000-line ceiling (#800 is the real split of that file and is a separate change).
 */

/** An axis-aligned LOCAL (anchor-relative, pre-rotation) rectangle. */
export interface LocalRect {
  readonly left: number;
  readonly right: number;
  readonly bottom: number;
  readonly top: number;
}

/** The smallest {@link LocalRect} containing both — the union, per axis. */
export function unionRect(a: LocalRect, b: LocalRect): LocalRect {
  return {
    left: Math.min(a.left, b.left),
    right: Math.max(a.right, b.right),
    bottom: Math.min(a.bottom, b.bottom),
    top: Math.max(a.top, b.top),
  };
}

/** A {@link LocalRect} as the 4 corners a corner builder returns, CCW from bottom-left. */
export function rectCorners(r: LocalRect): Local2[] {
  return [
    { x: r.left, y: r.bottom },
    { x: r.right, y: r.bottom },
    { x: r.right, y: r.top },
    { x: r.left, y: r.top },
  ];
}

/**
 * The LOCAL (anchor-relative, PRE-rotation) box rect for a given col/row + width/height —
 * the shared building block every text-like kind uses. `col`: 0=left-anchored (box extends +X),
 * 1=center-anchored, 2=right-anchored (box extends −X). `row`: 0=bottom-anchored (box extends
 * +Y — WCS Y-up, no display flip here), 1=middle-anchored, 2=top-anchored (box extends −Y).
 * `cad-core/leafBounds.ts::anchoredBoxCorners` reaches the SAME convention by delegating to
 * `wcsBoundsText.ts::mtextAnchoredBoxCorners`, which builds its rects here — one truth, not a
 * mirrored re-derivation.
 *
 * `sides` splits the already-in-`width` horizontal overhang into its two sides: the col
 * anchoring is applied to the UPRIGHT column `width − overhangLeft − overhangRight`, then each
 * side is pushed out by its own extent, so the returned rect is still exactly `width` wide but
 * sits where the ink actually is. A bigger `overhangLeft` therefore SHIFTS the rect left rather
 * than widening it, which is what carries `wcsBoundsText.ts`'s `MAX_GLYPH_LEFT_BEARING` onto the
 * correct side without touching the width. Absent ⇒ zero on both sides ⇒ the original formula, term for term.
 */
export function localBoxRect(
  width: number,
  height: number,
  col: number,
  row: number,
  sides?: { readonly overhangLeft: number; readonly overhangRight: number },
): LocalRect {
  const overhangLeft = sides?.overhangLeft ?? 0;
  const overhangRight = sides?.overhangRight ?? 0;
  const upright = width - overhangLeft - overhangRight;
  const uprightLeft = col === 0 ? 0 : col === 1 ? -upright / 2 : -upright;
  const localBottom = row === 0 ? 0 : row === 1 ? -height / 2 : -height;
  return {
    left: uprightLeft - overhangLeft,
    right: uprightLeft + upright + overhangRight,
    bottom: localBottom,
    top: localBottom + height,
  };
}

/** Rotate LOCAL (anchor-relative) points by `rotationDeg` and translate to `anchor` — the final step. */
export function anchorAndRotate(anchor: Local2, local: readonly Local2[], rotationDeg: number): Local2[] {
  return local.map((c) => {
    const r = rotateDeg2D(c.x, c.y, rotationDeg);
    return { x: anchor.x + r.x, y: anchor.y + r.y };
  });
}
